import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { productGroupApiSlice } from './productGroupApiSlice';

// Это создание адаптера сущностей с помощью Redux Toolkit, 
// который предоставляет стандартизированные методы для работы с коллекцией данных в Redux store.

// selectId: (group) => group._id
// Указывает, как извлекать уникальный идентификатор из каждого объекта группы товаров
// В MongoDB / Mongoose документы используют поле _id как первичный ключ
// Для каждого объекта группы будет использоваться его _id как ключ в хранилище

// sortComparer: (a, b) => a.name.localeCompare(b.name)
// Задает способ сортировки групп по умолчанию
// Сравнивает группы по полю name с учетом локали(правильная сортировка строк с учетом языка)
// Группы будут автоматически сортироваться по алфавиту
const productGroupsAdapter = createEntityAdapter({
	selectId: (group) => group._id,
	sortComparer: (a, b) => a.name.localeCompare(b.name)
});

const initialState = productGroupsAdapter.getInitialState({
	status: 'idle',
	error: null,
	currentGroup: null,
	selectedAttributes: []
});

const productGroupSlice = createSlice({
	name: 'productGroups',
	initialState,
	reducers: {
		setCurrentGroup: (state, action) => {
			state.currentGroup = action.payload;
		},
		addAttribute: (state, action) => {
			if (!state.currentGroup) return;
			state.currentGroup.attributes.push(action.payload);
		},
		updateAttribute: (state, action) => {
			const { index, attribute } = action.payload;
			if (!state.currentGroup?.attributes?.[index]) return;
			state.currentGroup.attributes[index] = attribute;
		},
		removeAttribute: (state, action) => {
			if (!state.currentGroup) return;
			state.currentGroup.attributes = state.currentGroup.attributes.filter(
				(_, idx) => idx !== action.payload
			);
		},
		selectAttributes: (state, action) => {
			state.selectedAttributes = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				productGroupApiSlice.endpoints.getProductGroups.matchPending,
				(state) => {
					state.status = 'loading';
				}
			)
			.addMatcher(
				productGroupApiSlice.endpoints.getProductGroups.matchFulfilled,
				(state, action) => {
					state.status = 'succeeded';
					productGroupsAdapter.setAll(state, action.payload);
				}
			)
			.addMatcher(
				productGroupApiSlice.endpoints.getProductGroups.matchRejected,
				(state, action) => {
					state.status = 'failed';
					state.error = action.error.message;
				}
			)
			.addMatcher(
				productGroupApiSlice.endpoints.createProductGroup.matchFulfilled,
				(state, action) => {
					productGroupsAdapter.addOne(state, action.payload);
				}
			)
			.addMatcher(
				productGroupApiSlice.endpoints.updateProductGroup.matchFulfilled,
				(state, action) => {
					productGroupsAdapter.upsertOne(state, action.payload);
				}
			)
			.addMatcher(
				productGroupApiSlice.endpoints.deleteProductGroup.matchFulfilled,
				(state, action) => {
					productGroupsAdapter.removeOne(state, action.payload._id);
				}
			);
	}
});

export const {
	setCurrentGroup,
	addAttribute,
	updateAttribute,
	removeAttribute,
	selectAttributes
} = productGroupSlice.actions;

export const {
	selectAll: selectAllProductGroups,
	selectById: selectProductGroupById,
	selectIds: selectProductGroupIds
} = productGroupsAdapter.getSelectors((state) => state.productGroups);

export const selectProductGroupsStatus = (state) => state.productGroups.status;
export const selectCurrentProductGroup = (state) => state.productGroups.currentGroup;
export const selectSelectedAttributes = (state) => state.productGroups.selectedAttributes;

export default productGroupSlice.reducer;